/** Clear of the anchor, per the design. */
export const ANCHOR_GAP = 8;

/** Matches `Dropdown`'s panel padding, so a clamped chip stops where its panels do. */
export const VIEWPORT_PADDING = 8;

export interface ChipPlacement {
    top: number;
    left: number;
    /** The chip is capped rather than allowed to run off the edge; its message truncates. */
    maxWidth: number;
}

export type AnchorRect = Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom' | 'width' | 'height'>;

export interface PlacementInput {
    anchorRect: AnchorRect;
    chipWidth: number;
    chipHeight: number;
    viewportWidth: number;
    viewportHeight: number;
}

/**
 * True when the anchor cannot be pointed at: detached, hidden, or scrolled out of view. A
 * zero-size rect covers a detached node and a `display: none` subtree alike, either of which
 * would strand the chip off-viewport — invisible, focused, and one Enter from confirming.
 */
export const isUnplaceable = (
    rect: AnchorRect,
    viewportWidth: number,
    viewportHeight: number,
): boolean =>
    (rect.width === 0 && rect.height === 0) ||
    rect.bottom < 0 ||
    rect.top > viewportHeight ||
    rect.right < 0 ||
    rect.left > viewportWidth;

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

/**
 * The chip sits to the anchor's left, its top edge on the anchor's midline rather than
 * centred on it — both measured off the frame. Both axes are then clamped into the viewport:
 * `isUnplaceable` deliberately keeps a part-visible anchor, so without this the chip could
 * hang off an edge with its focused confirm button out of reach.
 */
export const computeChipPlacement = ({
    anchorRect,
    chipWidth,
    chipHeight,
    viewportWidth,
    viewportHeight,
}: PlacementInput): ChipPlacement => ({
    top: clamp(
        anchorRect.top + anchorRect.height / 2,
        VIEWPORT_PADDING,
        viewportHeight - chipHeight - VIEWPORT_PADDING,
    ),
    left: clamp(
        anchorRect.left - ANCHOR_GAP - chipWidth,
        VIEWPORT_PADDING,
        viewportWidth - chipWidth - VIEWPORT_PADDING,
    ),
    maxWidth: Math.max(0, viewportWidth - VIEWPORT_PADDING * 2),
});

/** Every input `computeChipPlacement` reads, so an unchanged key means an unmoved chip. */
export const placementKey = (
    rect: AnchorRect,
    chipWidth: number,
    chipHeight: number,
    viewportWidth: number,
    viewportHeight: number,
): string =>
    `${rect.top}:${rect.left}:${rect.height}:${chipWidth}:${chipHeight}:${viewportWidth}:${viewportHeight}`;
